import type { StateStorage } from "zustand/middleware";

const isBrowser = () => typeof window !== "undefined";

export interface UrlParamsStorageOptions {
  /**
   * Persist each key as a bare query param (`?logic=abc`) instead of namespacing it by store
   * (`?form-link.logic=abc`).
   *
   * Use this when the URL shape is already fixed by something outside the store — an existing link
   * format, or a param another system reads. Because bare params are indistinguishable from anything
   * else in the query string, `keys` is required so the store knows which ones are its own.
   */
  keys?: readonly string[];
}

/**
 * Builds a zustand `StateStorage` that keeps persisted state in the URL's query string.
 *
 * Unlike the `localStorage`/`sessionStorage` backends, this one is readable and shareable: the state
 * travels with a copied link. That makes it the right choice for inputs that identify *which* view a
 * link opens, and the wrong choice for anything large, private, or high-frequency.
 *
 * Values are written one query param per key rather than as a single JSON blob — a URL that a person
 * may read or edit should stay legible. Keys are namespaced by store name unless `keys` opts out.
 * Writes go through `history.replaceState`, so persisting state never adds entries to the back
 * button.
 *
 * Pair it with `partialize` to select the subset of state that belongs in a URL; without one, the
 * whole store ends up in the address bar.
 */
export const createUrlParamsStorage = ({ keys }: UrlParamsStorageOptions = {}): StateStorage => {
  // Bare params carry no marker identifying the store, so the configured key list is the only way to
  // recognise them; namespaced mode can recognise its own by prefix.
  const ownParamNames = (name: string, params: URLSearchParams) =>
    keys ? keys.filter((key) => params.has(key)) : [...params.keys()].filter((key) => key.startsWith(`${name}.`));

  const paramNameFor = (name: string, key: string) => (keys ? key : `${name}.${key}`);
  const stateKeyFor = (name: string, paramName: string) => (keys ? paramName : paramName.slice(`${name}.`.length));

  return {
    getItem: (name) => {
      if (!isBrowser()) return null;

      const params = new URLSearchParams(window.location.search);
      const present = ownParamNames(name, params);
      if (present.length === 0) return null;

      // zustand hands the persist middleware a single string per store, so the per-key params are
      // recomposed into the JSON shape it expects.
      const state = present.reduce<Record<string, unknown>>((acc, paramName) => {
        acc[stateKeyFor(name, paramName)] = parseParamValue(params.get(paramName) ?? "");
        return acc;
      }, {});

      return JSON.stringify({ state, version: readVersion(params, name) });
    },

    setItem: (name, value) => {
      if (!isBrowser()) return;

      const parsed: unknown = JSON.parse(value);
      if (!isPersistedShape(parsed)) return;

      const params = new URLSearchParams(window.location.search);
      ownParamNames(name, params).forEach((paramName) => params.delete(paramName));

      Object.entries(parsed.state).forEach(([key, entry]) => {
        if (entry === undefined) return;
        params.set(paramNameFor(name, key), serializeParamValue(entry));
      });
      // The version marker is namespaced even in bare mode: it belongs to the store's bookkeeping
      // rather than to the URL contract the caller is matching.
      if (parsed.version !== undefined) params.set(`${name}._v`, String(parsed.version));

      replaceQuery(params);
    },

    removeItem: (name) => {
      if (!isBrowser()) return;

      const params = new URLSearchParams(window.location.search);
      ownParamNames(name, params).forEach((paramName) => params.delete(paramName));
      params.delete(`${name}._v`);
      replaceQuery(params);
    },
  };
};

/**
 * Strings round-trip as-is so a URL stays readable (`?form.logic=abc`, not `?form.logic=%22abc%22`);
 * everything else goes through JSON.
 */
const serializeParamValue = (value: unknown): string => (typeof value === "string" ? value : JSON.stringify(value));

const parseParamValue = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const readVersion = (params: URLSearchParams, name: string): number | undefined => {
  const raw = params.get(`${name}._v`);
  // A hand-authored URL carries no version marker; leaving it undefined avoids a spurious migration.
  if (raw === null) return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const replaceQuery = (params: URLSearchParams) => {
  const query = params.toString();
  const { pathname, hash } = window.location;
  window.history.replaceState(window.history.state, "", `${pathname}${query ? `?${query}` : ""}${hash}`);
};

const isPersistedShape = (value: unknown): value is { state: Record<string, unknown>; version?: number } =>
  typeof value === "object" && value !== null && "state" in value;
